import React, { useRef } from 'react';
import { Token } from 'react-bootstrap-typeahead';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';

const RBTToken = styled(Token)`
  &.rbt-token {
    font-size: 12px;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
`;

export interface TokenProps {
  children?: React.ReactNode | undefined;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => any;
  onRemove: (() => void) | undefined;
  option: string | { [key: string]: any };
  verticalList?: boolean;
}

interface CollectedProps {
  isDragging: boolean;
}

interface MonitorProps {
  isDragging: () => boolean;
}

const ItemTypes = {
  TOKEN: 'TOKEN',
};

/**
 * Custom Token with Drag and Drop support for the Typeahead input.
 */
export const TypeaheadDNDToken = ({ children, index, onMove, option, verticalList, ...props }: TokenProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [, drop] = useDrop({
    accept: ItemTypes.TOKEN,
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get horizontal middle
      const hoverMiddleX = verticalList
        ? (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
        : (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset() || { x: 0, y: 0 };

      // Get pixels to the top
      const hoverClientX = verticalList
        ? clientOffset.y - hoverBoundingRect.top
        : clientOffset.x - hoverBoundingRect.left;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging right
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      // Dragging left
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const sourceSpec = {
    type: ItemTypes.TOKEN,
    item: { type: ItemTypes.TOKEN, index, text: option },
    collect: (monitor: MonitorProps) => ({
      isDragging: monitor.isDragging(),
    }),
  };
  const [collected, drag] = useDrag(sourceSpec);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const { isDragging } = collected as CollectedProps;
  const opacity = isDragging ? 0.2 : 1;

  drag(drop(ref));

  return (
    <div ref={ref} draggable style={{ opacity }}>
      <RBTToken option={option} {...props}>
        {children}
      </RBTToken>
    </div>
  );
};
